import { useMemo } from "react";

import { useOrderDisputes } from "@/api/dispute-query";
import DisputeCard from "@/components/dispute/dispute-card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { createFileRoute, Outlet } from "@tanstack/react-router";

export const Route = createFileRoute("/vendor/dispute")({
	component: RouteComponent
});

function RouteComponent() {
	const { data, isError, isPending } = useOrderDisputes();

	const disputes = useMemo(() => {
		const resolved = data?.data ? data?.data?.filter(dispute => dispute.disputeStatus.toLowerCase().includes("resolved")) : [];
		const underReview = data?.data?.filter(dispute => dispute.disputeStatus.toLowerCase().includes("review")) ?? [];

		return { resolved, underReview };
	}, [data]);

	if (isPending) {
		return (
			<div className="w-full mx-auto p-6">
				<div className="flex flex-wrap gap-2">
					<div className="flex-1 w-full h-[80vh] overflow-hidden bg-white rounded-lg p-2">
						<div className="size-full flex items-center justify-center">
							<div className="w-full h-96 flex items-center justify-center">
								<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
	if (isError) {
		return (
			<div className="w-full mx-auto p-6">
				<div className="flex flex-wrap gap-2">
					<div className="flex-1 w-full h-[80vh] overflow-hidden bg-white rounded-lg p-2">
						<div className="flex size-full items-center justify-center">
							Error Fetching data
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="w-full mx-auto p-6 max-md:p-0">
			<div className="flex flex-wrap gap-2">
				<div className="flex-1 w-full h-[calc(100vh-2rem)] sm:h-[80vh] overflow-hidden bg-white rounded-lg p-4">
					<Tabs defaultValue="under-review" className="w-full">
						<TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent">
							<TabsTrigger
								value="under-review"
								className={cn(
									"rounded-none border-b-2 border-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent",
									"text-lg font-medium px-4 py-2"
								)}
							>
								Under review
								<span className="ml-2 text-sm bg-gray-200 px-2 py-0.5 rounded-full">
									{disputes.underReview.length}
								</span>
							</TabsTrigger>
							<TabsTrigger
								value="resolved"
								className={cn(
									"rounded-none border-b-2 border-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent",
									"text-lg font-medium px-4 py-2"
								)}
							>
								Resolved disputes
								{" "}
								<span className="ml-2 text-sm bg-gray-200 px-2 py-0.5 rounded-full">
									{disputes.resolved.length}
								</span>
							</TabsTrigger>
						</TabsList>

						<TabsContent value="under-review">
							<div className="mt-6 h-[80vh] scroll-pb-6 flex flex-col  gap-4 overflow-x-auto">
								{disputes.underReview.map((dispute, idx) => (
									<DisputeCard
										key={dispute.id}
										dispute={dispute}
										className={cn({
											"mb-32": idx === disputes.resolved.length - 1
										})}
									/>
								))}
								<div className="flex items-start gap-2 p-4 bg-gray-50 rounded-lg mt-4">
									<span className="text-gray-500">⚠️</span>
									<p className="text-sm text-gray-600">
										Orders you see here are currently under review due to a
										dispute. Please check back for updates or contact support
										for further assistance.
									</p>
								</div>
							</div>
						</TabsContent>
						<TabsContent value="resolved">
							<div className="mt-6 h-[80vh] flex flex-col gap-4 overflow-y-auto">
								{disputes.resolved.map((dispute, idx) => (
									<DisputeCard
										key={dispute.id}
										dispute={dispute}
										className={cn({
											"mb-32": idx === disputes.resolved.length - 1
										})}
									/>
								))}
							</div>
						</TabsContent>
					</Tabs>
				</div>
				<Outlet />
			</div>
		</div>
	);
}
